import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Providers = () => {
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [formData, setFormData] = useState({
    rif: '',
    nombre: '',
    direccion: '',
    telefono: '',
    email: '',
  });

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    const filtered = providers.filter(
      (p) =>
        p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.rif.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredProviders(filtered);
  }, [searchTerm, providers]);

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${API}/proveedores`);
      setProviders(response.data);
      setFilteredProviders(response.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error('Error al cargar proveedores');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProvider) {
        await axios.put(`${API}/proveedores/${editingProvider.id}`, formData);
        toast.success('Proveedor actualizado exitosamente');
      } else {
        await axios.post(`${API}/proveedores`, formData);
        toast.success('Proveedor creado exitosamente');
      }
      fetchProviders();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving provider:', error);
      toast.error('Error al guardar proveedor');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este proveedor?')) return;
    try {
      await axios.delete(`${API}/proveedores/${id}`);
      toast.success('Proveedor eliminado exitosamente');
      fetchProviders();
    } catch (error) {
      console.error('Error deleting provider:', error);
      toast.error('Error al eliminar proveedor');
    }
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setFormData({
      rif: provider.rif,
      nombre: provider.nombre,
      direccion: provider.direccion,
      telefono: provider.telefono,
      email: provider.email,
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingProvider(null);
    setFormData({ rif: '', nombre: '', direccion: '', telefono: '', email: '' });
  };

  return (
    <div className="p-8" data-testid="providers-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 font-heading">Proveedores</h1>
          <p className="text-sm text-slate-600 mt-2">Gestión de proveedores y contactos</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-provider-btn"
              className="bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg rounded-md px-6 py-2 font-bold tracking-wide"
              onClick={() => setEditingProvider(null)}
            >
              <Plus size={20} className="mr-2" />
              Nuevo Proveedor
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingProvider ? 'Editar Proveedor' : 'Nuevo Proveedor'}
              </DialogTitle>
              <DialogDescription>
                {editingProvider ? 'Actualizar información del proveedor' : 'Agregar un nuevo proveedor al sistema'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="rif">RIF</Label>
                <Input
                  id="rif"
                  data-testid="provider-rif-input"
                  value={formData.rif}
                  onChange={(e) => setFormData({ ...formData, rif: e.target.value })}
                  placeholder="J-12345678-9"
                  required
                />
              </div>
              <div>
                <Label htmlFor="nombre">Nombre</Label>
                <Input
                  id="nombre"
                  data-testid="provider-nombre-input"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="direccion">Dirección</Label>
                <Input
                  id="direccion"
                  data-testid="provider-direccion-input"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="telefono">Teléfono</Label>
                <Input
                  id="telefono"
                  data-testid="provider-telefono-input"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="provider-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseDialog}
                  data-testid="provider-cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="provider-submit-btn"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                >
                  {editingProvider ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-lg p-6">
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <Input
              data-testid="provider-search-input"
              type="text"
              placeholder="Buscar por nombre o RIF..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">RIF</th>
                <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Nombre</th>
                <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Dirección</th>
                <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Teléfono</th>
                <th className="p-4 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredProviders.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-slate-500">
                    No hay proveedores registrados
                  </td>
                </tr>
              ) : (
                filteredProviders.map((provider) => (
                  <tr
                    key={provider.id}
                    data-testid={`provider-row-${provider.id}`}
                    className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                  >
                    <td className="p-4 font-mono text-sm text-slate-700">{provider.rif}</td>
                    <td className="p-4 text-sm text-slate-900 font-medium">{provider.nombre}</td>
                    <td className="p-4 text-sm text-slate-600">{provider.direccion}</td>
                    <td className="p-4 font-mono text-sm text-slate-700">{provider.telefono}</td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          data-testid={`edit-provider-${provider.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(provider)}
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          data-testid={`delete-provider-${provider.id}`}
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(provider.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default Providers;